import React from 'react';
import { fmt } from '../../utils';

const NAVY = '#0b318b';
const CARD = { border: '1px solid #cbd5e1', borderRadius: 8, padding: 12, background: '#fff', minWidth: 0 };
const SEC = { fontSize: 11, fontWeight: 800, color: NAVY, letterSpacing: 0.4, marginBottom: 10, textTransform: 'uppercase' };
const LBL = { fontSize: 9.5, fontWeight: 700, letterSpacing: 0.3, color: '#64748b', textTransform: 'uppercase' };
const VAL = { fontSize: 11.5, fontWeight: 700, color: '#0f172a', marginTop: 2, wordBreak: 'break-word' };
const CELL = { border: '1px solid #cbd5e1', padding: '4px 7px', fontSize: 10.5, whiteSpace: 'nowrap' };
const HEAD = { ...CELL, background: '#243b64', color: '#fff', fontWeight: 700, fontSize: 9.5, letterSpacing: 0.3, textTransform: 'uppercase' };

const F = ({ label, value }) => (
  <div style={{ border: '1px solid #e2e8f0', borderRadius: 6, padding: '6px 9px', background: '#f8fafc' }}>
    <div style={LBL}>{label}</div>
    <div style={VAL}>{value === null || value === undefined || value === '' ? '—' : value}</div>
  </div>
);

// auto-fit rather than a fixed count: each half of the panel is only ~600px
// wide, and four rigid columns there squeeze labels onto three lines.
const Grid = ({ children, min = 150 }) => (
  <div style={{
    display: 'grid', gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))`, gap: 8,
  }}>{children}</div>
);

const Section = ({ n, title, children }) => (
  <div style={CARD}>
    <div style={SEC}>{n} — {title}</div>
    {children}
  </div>
);

/**
 * The expanded row on Voucher Review & Release.
 *
 * Read-only by design: every action lives on the row's button strip, so this
 * panel can never become a second place a voucher is edited. Sections 5 and 6
 * appear only for advance vouchers — an ordinary payment has no advance
 * information to show, and rendering empty panels would suggest it does.
 */
export default function VoucherReviewDetail({ voucher, history, loading, error }) {
  if (loading) return <div style={{ padding: 20, fontSize: 12, color: 'var(--text-secondary)' }}>Loading voucher detail…</div>;
  if (error) return <div style={{ padding: 20, fontSize: 12, color: 'var(--red)' }}>{error}</div>;
  if (!voucher) return null;

  const v = voucher;
  const items = v.items || [];

  return (
    <div style={{
      display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))',
      alignItems: 'start',
      gap: 12, padding: 12, background: '#f1f5f9',
      // Grid children default to min-content width, which lets a wide table
      // push the whole panel out. Both of these keep the columns honest.
      maxWidth: '100%', overflow: 'hidden',
    }}>
      {/* Explicit columns rather than grid auto-placement. Left holds section
          1; right stacks 2 above 3, so Supporting Documents fills the space
          under the short Supplier card instead of leaving it blank. */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
      <Section n="1" title="Voucher Information">
        <Grid>
          <F label="Voucher No." value={v.voucher_no} />
          <F label="Manual No." value={v.manual_voucher_no} />
          <F label="Voucher Date" value={v.voucher_date} />
          <F label="Payment Type" value={v.payment_type ? String(v.payment_type).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : null} />
          <F label="Payment Method" value={v.payment_method} />
          <F label="Status" value={(v.status || '').toUpperCase()} />
          <F label="Priority" value={v.priority} />
          <F label="Expense Category" value={v.expense_category_name} />
          <F label="Currency" value={v.currency} />
          <F label="Exchange Rate" value={v.exchange_rate} />
          <F label="Created By" value={v.created_by_name} />
          <F label="Posted By" value={v.posted_by_name} />
        </Grid>
        <div style={{ marginTop: 8 }}><F label="Narration" value={v.narration} /></div>
      </Section>

      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
      <Section n="2" title="Supplier Information">
        <Grid>
          <F label="Supplier Name" value={v.supplier_name} />
          <F label="Tax ID / TIN" value={v.supplier_tax_id} />
          <F label="Supplier Bank" value={v.supplier_bank_name} />
          <F label="Account Number" value={v.supplier_bank_account} />
        </Grid>
      </Section>

      <Section n="3" title="Supporting Documents">
        <Grid>
          <F label="Invoice No." value={v.invoice_no} />
          <F label="Invoice Date" value={v.invoice_date} />
          <F label="Requisition No." value={v.requisition_no || v.purchase_request_pr_number} />
          <F label="Delivery Note" value={v.delivery_note_no} />
          <F label="Approval Memo" value={v.approval_memo_no} />
          <F label="Purchase Order" value={v.po_number} />
          <F label="GRN" value={v.grn_number} />
          <F label="PJV" value={v.pjv_number} />
          <F label="JV" value={v.jv_number} />
          <F label="PI Number" value={v.pi_number} />
        </Grid>
      </Section>

      </div>

      <div style={{ ...CARD, gridColumn: '1 / -1', minWidth: 0 }}>
        <div style={SEC}>4 — Complete Voucher Line Items ({items.length})</div>
        {/* Scrolls on its own rather than widening the panel. minWidth:0 on the
            card is what actually permits the shrink — without it the grid
            child sizes to the table and the scrollbar never appears. */}
        <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
          <table style={{ borderCollapse: 'collapse', minWidth: 1200 }}>
            <thead><tr>
              {['Line', 'Item', 'Description', 'Spec', 'Line Vendor', 'Unit', 'Approved Qty', 'Prev. Paid',
                'Current Qty', 'Rate', 'Line Amount', 'Advance Alloc.', 'Item Code', 'Account Ledger',
                'Expense', 'Cost Centre', 'Project', 'VAT', 'WHT'].map(h => <th key={h} style={HEAD}>{h}</th>)}
            </tr></thead>
            <tbody>
              {items.map((it, i) => (
                <tr key={it.item_id || i}>
                  <td style={{ ...CELL, textAlign: 'center', fontWeight: 700 }}>{i + 1}</td>
                  <td style={CELL}>{it.material_name || '—'}</td>
                  <td style={{ ...CELL, whiteSpace: 'normal', minWidth: 180 }}>{it.description}</td>
                  <td style={CELL}>{it.specification || '—'}</td>
                  <td style={CELL}>{it.vendor_name || '—'}</td>
                  <td style={CELL}>{it.unit_of_measure || '—'}</td>
                  <td style={{ ...CELL, textAlign: 'right' }}>{it.approved_quantity ?? '—'}</td>
                  <td style={{ ...CELL, textAlign: 'right', color: '#2563eb' }}>{it.previously_paid_quantity ?? 0}</td>
                  <td style={{ ...CELL, textAlign: 'right', fontWeight: 700 }}>{it.process_quantity}</td>
                  <td style={{ ...CELL, textAlign: 'right' }}>{fmt(it.unit_rate)}</td>
                  <td style={{ ...CELL, textAlign: 'right', fontWeight: 700 }}>{fmt(it.line_amount)}</td>
                  <td style={{ ...CELL, textAlign: 'right', color: 'var(--accent)' }}>{fmt(it.advance_allocated_amount)}</td>
                  <td style={CELL}>{it.item_code || '—'}</td>
                  <td style={CELL}>{it.account_ledger_name || '—'}</td>
                  <td style={CELL}>{it.expense_category_name || '—'}</td>
                  <td style={CELL}>{it.cost_centre_name || '—'}</td>
                  <td style={CELL}>{it.project_name || '—'}</td>
                  <td style={{ ...CELL, textAlign: 'center' }}>{it.vat_applicable ? 'Yes' : 'No'}</td>
                  <td style={{ ...CELL, textAlign: 'center' }}>{it.wht_applicable ? 'Yes' : 'No'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {v.is_advance && (
        <Section n="5" title="Advance Payment Information">
          <Grid>
            <F label="Advance Flag" value="Yes" />
            <F label="Advance Amount" value={fmt(v.advance_amount)} />
            <F label="Advance %" value={v.advance_percent != null ? `${v.advance_percent}%` : '—'} />
            <F label="Allocation Method" value={v.advance_allocation_basis} />
            <F label="Recovery Method" value={v.advance_recovery_method} />
            <F label="Expected Delivery" value={v.expected_delivery_date} />
            <F label="Guarantee Ref" value={v.advance_guarantee_reference} />
            <F label="Performance Bond" value={v.performance_bond_reference} />
          </Grid>
          <div style={{ marginTop: 8 }}><F label="Advance Reason" value={v.advance_reason} /></div>
        </Section>
      )}

      {(v.advance_recoveries || []).length > 0 && (
        <Section n="6" title="Advance Recovery">
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>{['Advance Voucher', 'Amount Recovered', 'Recovered On'].map(h => <th key={h} style={HEAD}>{h}</th>)}</tr></thead>
            <tbody>
              {v.advance_recoveries.map(r => (
                <tr key={r.recovery_id}>
                  <td style={{ ...CELL, fontFamily: 'var(--mono)', color: NAVY, fontWeight: 700 }}>{r.advance_voucher_no || '—'}</td>
                  <td style={{ ...CELL, textAlign: 'right', fontWeight: 700 }}>{fmt(r.amount)}</td>
                  <td style={{ ...CELL, textAlign: 'center' }}>{r.recovered_on || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ marginTop: 8 }}><F label="Total Recovered On This Voucher" value={fmt(v.advance_recovered_amount)} /></div>
        </Section>
      )}

      <Section n="7" title="Payment Summary">
        <Grid>
          <F label="Lines Selected" value={items.length} />
          <F label="Subtotal" value={fmt(v.subtotal_amount)} />
          <F label="Discount" value={fmt(v.less_discount)} />
          <F label="Retention" value={fmt(v.less_retention)} />
          <F label="Other Additions" value={fmt(v.other_additions)} />
          <F label="Gross Amount" value={fmt(v.gross_amount)} />
          <F label={`VAT (${v.vat_percent}%)`} value={fmt(v.vat_amount)} />
          <F label={`WHT (${v.wht_percent}%)`} value={fmt(v.wht_amount)} />
          <F label="Withheld VAT" value={fmt(v.withheld_vat)} />
          <F label="Other Deductions" value={fmt(v.other_deductions)} />
          <F label="Advance Recovered" value={fmt(v.advance_recovered_amount)} />
          <F label="Amount Payable" value={fmt(v.amount_payable)} />
          <F label="Net Payable" value={fmt(v.net_payable)} />
          <F label="Amount Processed" value={fmt(v.amount_processed)} />
          <F label="Amount Paid" value={fmt(v.amount_paid)} />
          <F label="Outstanding" value={fmt(Number(v.net_payable || 0) - Number(v.amount_paid || 0))} />
        </Grid>
      </Section>

      <Section n="8" title="Review &amp; Processing History">
        <Grid min={200}>
          <F label="Posted By" value={v.posted_by_name} />
          <F label="Posted Date" value={v.posted_at ? String(v.posted_at).slice(0, 10) : null} />
          <F label="Approved By" value={v.approved_by_name} />
          <F label="Approval Date" value={v.approved_at ? String(v.approved_at).slice(0, 10) : null} />
          <F label="Released By" value={v.released_by_name} />
          <F label="Release Date" value={v.released_at ? String(v.released_at).slice(0, 10) : null} />
        </Grid>
        {v.review_on_hold && (
          <div style={{ marginTop: 8, background: '#fff7ed', border: '1px solid #fdba74', borderRadius: 6, padding: '7px 10px', fontSize: 11, color: '#7c2d12' }}>
            <strong>On hold</strong>{v.review_hold_reason ? ` — ${v.review_hold_reason}` : ''}
          </div>
        )}

        <div style={{ ...SEC, marginTop: 14, marginBottom: 6 }}>Status History</div>
        {(history || []).length === 0 ? (
          <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>No workflow history recorded yet.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>{['Date', 'Action', 'User', 'Step', 'Comment'].map(h => <th key={h} style={HEAD}>{h}</th>)}</tr></thead>
              <tbody>
                {/* Field names must match WorkflowTrackingHistorySchema:
                    date_actioned / status / approver_fullname / comments. */}
                {history.map((h, i) => (
                  <tr key={h.step_id || i}>
                    <td style={CELL}>{(h.date_actioned || '').slice(0, 16).replace('T', ' ') || '—'}</td>
                    <td style={{ ...CELL, fontWeight: 700, textTransform: 'capitalize' }}>{h.status || h.action || '—'}</td>
                    <td style={CELL}>{h.approver_fullname || '—'}</td>
                    <td style={CELL}>{h.step_name || '—'}</td>
                    <td style={{ ...CELL, whiteSpace: 'normal', minWidth: 200 }}>{h.comments || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </div>
  );
}
